import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search, Filter, UserPlus, Download, Grid, List,
  Mail, Phone, MapPin, Building2, Calendar, MoreVertical,
  Edit2, Trash2, Eye, UserCog, FileText, MessageSquare
} from 'lucide-react';
import { employeeData } from '../data/mockData';

const EmployeeCard = ({ employee, onView, onEdit }) => (
  <motion.div
    whileHover={{ scale: 1.01 }}
    className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all"
  >
    <div className="flex items-center gap-4 mb-4">
      <div className="w-16 h-16 rounded-xl bg-indigo-100 flex items-center justify-center">
        <span className="text-xl font-semibold text-indigo-600">
          {employee.name.split(' ').map(n => n[0]).join('')}
        </span>
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">{employee.name}</h3>
        <p className="text-sm text-gray-500">{employee.position}</p>
        <p className="text-sm text-gray-500">{employee.department}</p>
      </div>
    </div>

    <div className="space-y-2 mb-4">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Mail className="w-4 h-4" />
        {employee.email}
      </div>
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Phone className="w-4 h-4" />
        {employee.phone}
      </div>
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <MapPin className="w-4 h-4" />
        {employee.location}
      </div>
    </div>

    <div className="flex gap-2 pt-4 border-t">
      <button
        onClick={() => onView(employee.id)}
        className="flex-1 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 
          transition-colors text-sm font-medium"
      >
        View Profile
      </button>
      <button
        onClick={() => onEdit(employee.id)}
        className="flex-1 py-2 text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 
          transition-colors text-sm font-medium"
      >
        Edit
      </button>
    </div>
  </motion.div>
);

const EmployeeListItem = ({ employee, onView, onEdit }) => (
  <div className="flex items-center justify-between p-4 bg-white rounded-xl hover:bg-gray-50 transition-colors">
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
        <span className="text-lg font-semibold text-indigo-600">
          {employee.name.split(' ').map(n => n[0]).join('')}
        </span>
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">{employee.name}</h3>
        <p className="text-sm text-gray-500">{employee.position}</p>
      </div>
    </div>

    <div className="flex items-center gap-8">
      <div className="hidden md:block">
        <div className="text-sm text-gray-600">{employee.department}</div>
        <div className="text-sm text-gray-500">{employee.location}</div>
      </div>
      <div className="hidden md:block">
        <div className="text-sm text-gray-600">{employee.email}</div>
        <div className="text-sm text-gray-500">{employee.phone}</div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onView(employee.id)}
          className="p-2 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
        >
          <Eye className="w-5 h-5" />
        </button>
        <button
          onClick={() => onEdit(employee.id)}
          className="p-2 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
        >
          <Edit2 className="w-5 h-5" />
        </button>
        <div className="relative group">
          <button className="p-2 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors">
            <MoreVertical className="w-5 h-5" />
          </button>
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 
            hidden group-hover:block z-10">
            <div className="py-2">
              <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 
                flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Manage Access
              </button>
              <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 
                flex items-center gap-2">
                <FileText className="w-4 h-4" />
                View Documents
              </button>
              <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 
                flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Send Message
              </button>
              <div className="border-t my-1"></div>
              <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 
                flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const EmployeeModule = ({ isMobile }) => {
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const handleView = (id) => {
    // Navigate to employee detail view
    console.log('View employee:', id);
  };

  const handleEdit = (id) => {
    // Navigate to employee edit form
    console.log('Edit employee:', id);
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex-1 min-w-[200px] max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 
                focus:ring-indigo-500"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="All">All Departments</option>
            {/* Add department options */}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="On Leave">On Leave</option>
            <option value="Inactive">Inactive</option>
          </select>
          <div className="flex items-center gap-1 border border-gray-200 rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' 
                ? 'bg-indigo-50 text-indigo-600' 
                : 'text-gray-400 hover:text-indigo-600 hover:bg-gray-50'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' 
                ? 'bg-indigo-50 text-indigo-600' 
                : 'text-gray-400 hover:text-indigo-600 hover:bg-gray-50'}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Employee Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {employeeData.employees?.map((employee) => (
            <EmployeeCard
              key={employee.id}
              employee={employee}
              onView={handleView}
              onEdit={handleEdit}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {employeeData.employees?.map((employee) => (
            <EmployeeListItem
              key={employee.id}
              employee={employee}
              onView={handleView}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EmployeeModule; 